import "./GroupSelectList.scss";

import React, { useState, useCallback } from "react";

import AdvancedSearchFilter, {
	flattenAdvancedOptions,
	AdvancedOption,
} from "../../../components/common/AdvancedSearchFilter/AdvancedSearchFilter";
import Button from "../../../components/common/Button/Button";

import { useSelector } from "../../../lib/hook/ReduxHooks";
import * as ScopeSelector from "../../../store/Scope/ScopeSelectors";
import * as FleetSelector from "../../../store/Fleet/Fleet.selector";
import * as strings from "../../../strings";
import * as AuthUtils from "../../../lib/util/AuthUtils";
import * as GroupUtils from "../../../lib/util/GroupUtils";

interface GroupSelectListProps {
	defaultValue?: string[];
	onChange?: (groupIds: string[]) => void;
}

const GroupSelectList: React.FC<GroupSelectListProps> = (props) => {
	const { defaultValue, onChange } = props;

	const { fleet, groups } = useSelector((state) => ({
		fleet: FleetSelector.selectCurrentScopeFleet(state).value,
		groups: ScopeSelector.selectCurrentScopeGroupsAvailableToUser(state).value,
	}));

	const [selectedGroups, setSelectedGroups] = useState<string[]>(
		defaultValue || [],
	);
	const hasRootAccess = AuthUtils.canAccessRoot();

	const parseOptions = (groups: any, depth = 0): AdvancedOption[] => {
		if (hasRootAccess && depth === 0) {
			return [
				{
					depth: 0,
					title: fleet?.name || "",
					value: "__fleet",
					spaced: false,
					children: parseOptions(groups, depth + 1),
				},
			];
		}

		return (
			groups?.map((group: any) => ({
				depth: hasRootAccess ? depth + 1 : depth,
				title: group.name,
				value: group.id,
				spaced: false,
				children: group.groups ? parseOptions(group.groups, depth + 1) : [],
			})) || []
		);
	};

	const options = parseOptions(groups || []);
	const flatOptions = flattenAdvancedOptions(options);

	const onSelectedGroupsChange = useCallback(
		(id, newSelectedGroups) => {
			setSelectedGroups(newSelectedGroups || []);

			if (onChange) {
				onChange(newSelectedGroups || []);
			}
		},
		[onChange],
	);

	const onSelectAll = useCallback(() => {
		const allGroupIds = flatOptions
			? flatOptions.map((group) => group.value)
			: [];
		setSelectedGroups(allGroupIds);

		if (onChange) {
			onChange(allGroupIds);
		}
	}, [flatOptions, onChange]);

	const onClear = useCallback(() => {
		setSelectedGroups([]);

		if (onChange) {
			onChange([]);
		}
	}, [onChange]);

	const checker = useCallback(
		(groupId: string) => {
			return selectedGroups.includes(groupId);
		},
		[selectedGroups],
	);

	const indeterminateChecker = useCallback(
		(groupId: string): boolean => {
			const group = flatOptions.find(
				(flatGroup) => flatGroup.value === groupId,
			);
			const isChecked = checker(groupId);

			if (!isChecked || !group?.children) {
				return false;
			}

			const indeterminate = !group.children.every((childGroup) => {
				let valid =
					!selectedGroups || selectedGroups.includes(childGroup.value);

				if (valid && childGroup.children) {
					valid = !indeterminateChecker(childGroup.value);
				}

				return valid;
			});

			return indeterminate;
		},
		[selectedGroups, flatOptions, checker],
	);

	return (
		<div className={"GroupSelectList"}>
			<div className={"GroupSelectList__selected-groups"}>
				{selectedGroups.map((groupId, index) => {
					const group = GroupUtils.findGroupById(groups, groupId);
					return (
						<span>
							{index === 0 ? "" : ", "}
							{group?.name || groupId}
						</span>
					);
				})}
			</div>

			<AdvancedSearchFilter
				noInput={true}
				advancedCtaCopy={strings.FORM_GROUP_SELECT_LIST_CTA}
				advancedProps={[
					{
						id: "group",
						title: strings.FORM_GROUP_SELECT_LIST_TITLE,
						visible: true,
						property: "group",
						options: options,
						onSelectAll,
						onClear,
						checker,
						indeterminateChecker,
						onChange: onSelectedGroupsChange,
						selectedOptions: selectedGroups,
						withoutSubOptsTitle:
							strings.DRIVERS_SEARCH_FILTER_WITHOUT_SUB_GROUPS,
					},
				]}
			/>
		</div>
	);
};

export default GroupSelectList;
